import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGlobal } from 'context/GlobalContext';
import { useToast } from 'context/ToastContext';
import { defaultObjectStatuses, defaultSaleStatuses, useObject } from '../context/ObjectProvider';
import * as apiObject from 'api/apiObjects';
import { useListRefreshEffect } from 'utils/helpers/list/useListRefreshEffect';
import { useListOnPage } from 'utils/helpers/list/useListOnPage';
import type { GetObjectListParamsType, GetObjectListResultType, ObjectType } from '@econtractor/types';

const buildObjectsOrder = (order: string, desc: boolean) =>
    `${order ? `order by ${order} ` : ''}${desc ? 'DESC' : ''}`;

const normalizePaginatorPage = (value: unknown): number => {
    const raw = Array.isArray(value) ? value[0] : value;
    const page = Number(raw);
    return Number.isFinite(page) && page !== 0 ? page : 1;
};

const useObjectsList = () => {
    const { t } = useTranslation(['Objects', 'common']);
    const { g } = useGlobal();
    const { showToast } = useToast();
    const {
        showSpinner,
        setShowSpinner,
        objectsDesc,
        objectsOrder,
        filterStatus,
        setFilterStatus,
        filterTeamId,
        setFilterTeamId,
        filterClientId,
        setFilterClientId,
        filterOnlyOverdue,
        setFilterOnlyOverdue,
        setObjectsOrder,
        setObjectsDesc,
        showDebtor,
        listResetNonce,
        listRefreshNonce,
        onlySale,
        onlyService,
    } = useObject();

    const [objectsList, setObjectsList] = useState<ObjectType[] | null>(null);
    const [paidUsd, setPaidUsd] = useState(0);
    const [paidUah, setPaidUah] = useState(0);
    const [page, setPage] = useState<number | null>(null);
    const [clientInfoId, setClientInfoId] = useState<number | null>(null);
    const [tasksObjectId, setTasksObjectId] = useState<number | null>(null);
    const [listFetchNonce, setListFetchNonce] = useState(0);

    const location = useLocation();
    const onPage = useListOnPage();
    const prevListData = useRef<string | null>(null);
    const forceNextFetchRef = useRef(false);

    const fetchObjects = async (force = false, options?: { silent?: boolean }): Promise<void | null> => {
        if (force) prevListData.current = null;
        const search = `%${g?.search?.[location.pathname] || ''}%`;
        if (!page || showSpinner) return null;

        const listData: GetObjectListParamsType = {
            search,
            order: buildObjectsOrder(objectsOrder, objectsDesc),
            page: page < 0 ? 1 : normalizePaginatorPage(page),
            onPage,
            status: filterStatus,
            ...(filterTeamId ? { teamId: filterTeamId } : {}),
            ...(filterClientId ? { clientId: filterClientId } : {}),
            ...(!onlySale && !onlyService && filterOnlyOverdue ? { onlyOverdue: true } : {}),
            onlySale,
            onlyService,
        };

        // Запобіжник для подвійного запиту
        if (prevListData.current === JSON.stringify(listData)) return null;
        prevListData.current = JSON.stringify(listData);

        const silent = Boolean(options?.silent);
        if (!silent) setShowSpinner(true);
        const resObjects = (await apiObject.getList(listData)) as GetObjectListResultType | null;
        if (!resObjects) {
            if (!silent) setShowSpinner(false);
            showToast(t('common:messError'), 'danger');
            return null;
        }
        if (!silent) setShowSpinner(false);

        g.setPaginator((prev: Record<string, unknown>) => ({
            ...prev,
            [location.pathname]: resObjects.paginator,
        }));

        setObjectsList(resObjects.objects);
        setPaidUsd(Number(resObjects.paidUsd || 0));
        setPaidUah(Number(resObjects.paidUah || 0));
        return null;
    };

    const bumpListFetch = useCallback(() => {
        prevListData.current = null;
        forceNextFetchRef.current = true;
        setListFetchNonce((nonce) => nonce + 1);
    }, []);

    const resetObjectsList = useCallback(() => {
        setObjectsOrder('');
        setObjectsDesc(false);
        setFilterStatus(onlySale || onlyService ? [...defaultSaleStatuses] : [...defaultObjectStatuses]);
        setFilterTeamId(null);
        setFilterClientId(null);
        setFilterOnlyOverdue(false);
        g.setPaginatorPage(location.pathname, 1);
        setPage(1);
        bumpListFetch();
    }, [
        bumpListFetch,
        g,
        location.pathname,
        setFilterClientId,
        setFilterOnlyOverdue,
        setFilterStatus,
        setFilterTeamId,
        setObjectsDesc,
        setObjectsOrder,
        onlySale,
        onlyService,
    ]);

    useListRefreshEffect(listResetNonce, () => {
        resetObjectsList();
    });

    useListRefreshEffect(listRefreshNonce, () => {
        bumpListFetch();
    });

    // Хук на пошук
    useEffect(() => {
        if (page === 1) {
            void fetchObjects(true);
        } else {
            setPage(1);
        }
    }, [g.search?.[location.pathname], g.target]);

    // Хук на зміну сторінки / фільтрів / refresh|reset
    useEffect(() => {
        const force = forceNextFetchRef.current;
        forceNextFetchRef.current = false;
        void fetchObjects(force);
    }, [page, filterStatus, filterTeamId, filterClientId, filterOnlyOverdue, objectsOrder, objectsDesc, onPage, listFetchNonce]);

    // Змінюємо "внутрішню сторінку" при зміні пагінатора
    useEffect(() => {
        const _page = normalizePaginatorPage(g?.paginator?.[location.pathname]?.currentPage);
        if (page !== _page) setPage(_page);
    }, [
        g?.paginator?.[location.pathname]?.currentPage,
        g?.paginator?.[location.pathname]?.countRows,
        g?.paginator?.[location.pathname]?.onPage,
    ]);

    const tasksContextName =
        objectsList?.find((item) => item.ID === tasksObjectId)?.objectName
        || objectsList?.find((item) => item.ID === tasksObjectId)?.clientName
        || null;

    return {
        objectsList,
        paidUsd,
        paidUah,
        showDebtor,
        clientInfoId,
        setClientInfoId,
        tasksObjectId,
        setTasksObjectId,
        tasksContextName,
        fetchObjects,
    };
};

export default useObjectsList;
